import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PropertiesManagementService,
  PropertiesService,
  RealProperty,
  User,
  UserRole,
  UserSummary
} from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { NgIf } from '@angular/common';
import { InputComponent } from '../ui-forms/input/input.component';

@Component({
  selector: 'app-property-referent-association',
  templateUrl: './property-referent-association.component.html',
  styleUrls: ['./property-referent-association.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, ReactiveFormsModule, MatProgressSpinnerModule, TranslateModule, InputComponent]
})
export class PropertyReferentAssociationComponent implements OnInit, OnDestroy {
  @Input() type: 'referent' | 'notary' = 'referent';
  @Input() prop: RealProperty;

  readonly USER_ROLES_ALLOWED = { users: [] };
  loadPropertyReferentsSub = new Subscription();
  addPropertyReferentsSub = new Subscription();
  delPropertyReferentsSub = new Subscription();
  isLoading = false;
  referent: UserSummary;
  autoCompletedOptions: User[];
  titleText;

  form = this.fb.group({
    referent: ['']
  });

  constructor(
    private fb: UntypedFormBuilder,
    private propertiesService: PropertiesService,
    private propertiesManagementService: PropertiesManagementService
  ) {}

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.form.get(field);
  }

  ngOnInit(): void {
    this.USER_ROLES_ALLOWED.users = this.type === 'notary' ? [UserRole.NOTARY] : [UserRole.REAL_ESTATE];
    this.titleText = this.type === 'notary' ? 'propertyNotaryAssociation' : 'propertyReferentAssociation';
    this.referent = this.type === 'notary' ? this.prop.details?.notary : this.prop.details?.referent;
    this.f('referent').setValue(this.referent);
  }

  ngOnDestroy(): void {
    this.loadPropertyReferentsSub.unsubscribe();
    this.addPropertyReferentsSub.unsubscribe();
    this.delPropertyReferentsSub.unsubscribe();
  }

  onAddUser(user: User) {
    this.referent = user as UserSummary;
    this.f('referent').setValue(this.referent);
    this.addReferentUser(user.uid);
  }

  onDeleteUser() {
    this.removeReferentUser();
    this.referent = null;
    this.f('referent').setValue(null);
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //

  addReferentUser(userId: string) {
    this.isLoading = true;
    if (this.type === 'notary') {
      this.addPropertyReferentsSub = this.propertiesManagementService
        .updateRealPropertyNotary(this.prop.uid, userId)
        .subscribe({
          next: (res: void) => {
            this.isLoading = false;
          }
        });
    } else {
      this.addPropertyReferentsSub = this.propertiesManagementService
        .updateRealPropertyReferent(this.prop.uid, userId)
        .subscribe({
          next: (res: void) => {
            this.isLoading = false;
          }
        });
    }
  }

  removeReferentUser() {
    this.isLoading = true;
    if (this.type === 'notary') {
      this.delPropertyReferentsSub = this.propertiesManagementService
        .deleteRealPropertyNotary(this.prop.uid)
        .subscribe({
          next: (res: void) => {
            this.isLoading = false;
          }
        });
    } else {
      this.delPropertyReferentsSub = this.propertiesManagementService
        .deleteRealPropertyReferent(this.prop.uid)
        .subscribe({
          next: (res: void) => {
            this.isLoading = false;
          }
        });
    }
  }
}
